<template>
  <div class="question-view img-drag" ref="questionView">
    <FixedFontSizeSelector class="fixedFontSizeSelector">
      <div class="quest" :style="`${mode == 'viewAnswer' ? `--beforeShow: '';` : `--beforeShow: none;`}`">

        <div class="title">
          <vue-mathjax @onRenderComplete="onRenderComplete(++mathjaxNum)" :formula="form.title" :safe="false"/>
        </div>

        <div class="img-show">
          <div v-drag-img="{self, className: '.block-style', idx}"
               :class="['single', existImage.includes(idx) && 'disabled', curClickSelectImage == idx && 'cur-select']"
               v-for="(item, idx) in imageList" :key="idx"
               @mousedown="clickEvent($event, idx)">
            <img :src="item.image" v-if="item.image" alt="">
          </div>
        </div>

      </div>
    </FixedFontSizeSelector>
    <div class="quest-img" :style="{backgroundImage: `url(${form.img_url})`}">
      <img :src="form.img_url" v-if="form.img_url" alt="">
    </div>
  </div>
</template>

<script>
import ImgDrag from './imgDrag/imgDrag';
import { _debounce } from '@/utils/throttle.js'
import {getNodeOffset} from '@/utils/scroll-to'
import FixedFontSizeSelector from '@/views/examination/components/dialog/FontSizeSelector'

// 拖拽指令重写：修复iOS残留、事件覆盖、坐标兼容问题
let dragText = {
  bind(el, binding) {
    bindMobile(el, binding)
    bindPc(el, binding)
  },
  unbind(el) {
    // 指令销毁时清理事件和残留
    clearDrag(el)
  }
}

// 清理单个元素的拖拽事件和全局残留
function clearDrag(el) {
  if (el._touchStartHandler) {
    el.removeEventListener('touchstart', el._touchStartHandler)
    el._touchStartHandler = null
  }
  if (el._mouseDownHandler) {
    el.removeEventListener('mousedown', el._mouseDownHandler)
    el._mouseDownHandler = null
  }
  // 清理全局残留副本
  document.querySelectorAll('.drag-img-copy').forEach(node => node.remove())
  // 移除全局事件
  document.body.removeEventListener('touchmove', el._touchMoveHandler)
  document.body.removeEventListener('touchend', el._touchEndHandler)
  document.body.removeEventListener('touchcancel', el._touchEndHandler)
  document.body.removeEventListener('mousemove', el._mouseMoveHandler)
  document.body.removeEventListener('mouseup', el._mouseUpHandler)
}

// ========== 移动端拖拽重写 ==========
function bindMobile(el, binding) {
  el._touchStartHandler = function(e) {
    e.preventDefault()
    e.stopPropagation()

    const { className, self, idx } = binding.value
    if (self.existImage.includes(idx)) return

    // 兜底：拖拽开始前先清理所有残留副本
    document.querySelectorAll('.drag-img-copy').forEach(node => node.remove())

    // 标准方式计算元素页面坐标，替代非标准的 layerX/layerY
    const rect = el.getBoundingClientRect()
    const touch = e.touches[0]
    const offsetX = touch.clientX - rect.left
    const offsetY = touch.clientY - rect.top
    const elPageX = rect.left + window.scrollX
    const elPageY = rect.top + window.scrollY
    const elWidth = el.offsetWidth
    const elHeight = el.offsetHeight

    // 预计算所有落点容器区域
    const containers = document.querySelectorAll(className)
    let containIdx = null
    const containArea = []
    for (let i = 0; i < containers.length; i++) {
      const container = containers[i]
      const { left, top } = getNodeOffset(container)
      const width = container.offsetWidth
      const height = container.offsetHeight
      containArea.push([left, top, left + width, top + height])
    }

    // 创建拖拽副本，加专属类名方便统一清理
    const copyEl = el.cloneNode(true)
    copyEl.className = copyEl.className + ' drag-img-copy'
    copyEl.style.cssText = `
      position: absolute;
      top: ${elPageY}px;
      left: ${elPageX}px;
      width: ${elWidth}px;
      height: ${elHeight}px;
      z-index: 9999;
      pointer-events: none;
    `
    const img = copyEl.querySelector('img')
    if (img) {
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: contain;
        user-select: none;
        -webkit-user-drag: none;
        -webkit-touch-callout: none;
      `
    }
    document.body.appendChild(copyEl)

    // 触摸移动
    el._touchMoveHandler = function(e) {
      e.preventDefault()
      e.stopPropagation()
      const moveTouch = e.touches[0]
      const moveX = moveTouch.pageX - offsetX
      const moveY = moveTouch.pageY - offsetY

      copyEl.style.left = `${moveX}px`
      copyEl.style.top = `${moveY}px`

      // 判断落点
      containIdx = null
      for (let i = 0; i < containArea.length; i++) {
        const [l, t, r, b] = containArea[i]
        if (moveTouch.pageX >= l && moveTouch.pageY >= t && moveTouch.pageX <= r && moveTouch.pageY <= b) {
          containIdx = i
          break
        }
      }
    }

    // 触摸结束/取消统一清理
    el._touchEndHandler = function() {
      if (containIdx != null) {
        self.drag.assignment(containers[containIdx], idx, self)
      }
      // 移除副本
      if (copyEl.parentNode) copyEl.parentNode.removeChild(copyEl)
      // 解绑全局事件
      document.body.removeEventListener('touchmove', el._touchMoveHandler)
      document.body.removeEventListener('touchend', el._touchEndHandler)
      document.body.removeEventListener('touchcancel', el._touchEndHandler)
      el._touchMoveHandler = null
      el._touchEndHandler = null
    }

    // 同时绑定结束和取消事件
    document.body.addEventListener('touchmove', el._touchMoveHandler, { passive: false })
    document.body.addEventListener('touchend', el._touchEndHandler)
    document.body.addEventListener('touchcancel', el._touchEndHandler)
  }

  el.addEventListener('touchstart', el._touchStartHandler, { passive: false })
}

// ========== PC端拖拽重写（同步修复鼠标移出窗口残留问题） ==========
function bindPc(el, binding) {
  el._mouseDownHandler = function(e) {
    const { className, self, idx } = binding.value
    if (self.existImage.includes(idx)) return

    // 兜底清理残留
    document.querySelectorAll('.drag-img-copy').forEach(node => node.remove())

    const rect = el.getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const offsetY = e.clientY - rect.top
    const elPageX = rect.left + window.scrollX
    const elPageY = rect.top + window.scrollY
    const elWidth = el.offsetWidth
    const elHeight = el.offsetHeight

    const containers = document.querySelectorAll(className)
    let containIdx = null
    const containArea = []
    for (let i = 0; i < containers.length; i++) {
      const container = containers[i]
      if (container.dataset.mislead == 'mislead') continue
      const { left, top } = getNodeOffset(container)
      const width = container.offsetWidth
      const height = container.offsetHeight
      containArea.push([left, top, left + width, top + height])
    }

    const copyEl = el.cloneNode(true)
    copyEl.className = copyEl.className + ' drag-img-copy'
    copyEl.style.cssText = `
      position: absolute;
      top: ${elPageY}px;
      left: ${elPageX}px;
      width: ${elWidth}px;
      height: ${elHeight}px;
      z-index: 9999;
      pointer-events: none;
    `
    const img = copyEl.querySelector('img')
    if (img) {
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: contain;
        user-select: none;
        -webkit-user-drag: none;
      `
    }
    document.body.appendChild(copyEl)

    el._mouseMoveHandler = function(e) {
      const moveX = e.pageX - offsetX
      const moveY = e.pageY - offsetY
      copyEl.style.left = `${moveX}px`
      copyEl.style.top = `${moveY}px`

      containIdx = null
      for (let i = 0; i < containArea.length; i++) {
        const [l, t, r, b] = containArea[i]
        if (e.pageX >= l && e.pageY >= t && e.pageX <= r && e.pageY <= b) {
          containIdx = i
          break
        }
      }
    }

    el._mouseUpHandler = function() {
      if (containIdx != null) {
        self.drag.assignment(containers[containIdx], idx, self)
      }
      if (copyEl.parentNode) copyEl.parentNode.removeChild(copyEl)
      document.body.removeEventListener('mousemove', el._mouseMoveHandler)
      document.body.removeEventListener('mouseup', el._mouseUpHandler)
      document.removeEventListener('mouseleave', el._mouseUpHandler)
      el._mouseMoveHandler = null
      el._mouseUpHandler = null
    }

    document.body.addEventListener('mousemove', el._mouseMoveHandler)
    document.body.addEventListener('mouseup', el._mouseUpHandler)
    // 鼠标拖出浏览器窗口也触发结束，避免残留
    document.addEventListener('mouseleave', el._mouseUpHandler)
  }

  el.addEventListener('mousedown', el._mouseDownHandler)
}

export default {
  components: {
    FixedFontSizeSelector
  },
  props: ['form', 'mode'],
  directives: {
    'drag-img': dragText
  },
  data() {
    return {
      mathjaxNum: 0,
      image: '',
      topic: '',
      imageList: [],
      curClickSelectImage: null,
      existImage: [],
      emptyBlock: [],
      map: [],

      drag: null,
      answer_results: {
        id: null,
        student_choices: []
      },
      recordQuestion: {},
      self: this,
      limit: 0
    }
  },
  watch: {
    'form.id': {
      handler() {
        this.initData()
      },
      deep: true
    }
  },
  beforeDestroy() {
    // 组件销毁强制清理所有拖拽残留
    document.querySelectorAll('.drag-img-copy').forEach(node => node.remove())
    window.onresize = null
  },
  methods: {
    onRenderComplete(i) {
      let num = 1
      if(i == num) {
        this.$parent.loading = false
      }
    },
    resetting() {
      this.$emit('permissions', false)
      this.limit = 0
      let blocks = document.querySelectorAll('.block-style')
      for(let i = 0; i < blocks.length; i++) {
        blocks[i].click()
      }

      this.recordQuestion = {}
      this.answer_results = {
        id: null,
        student_choices: []
      }
      this.repariRecordQuestion()
    },
    repariRecordQuestion() {
      this.$emit('repariRecordQuestion', this.recordQuestion, this.limit)
    },
    showTrueFalse() {
      let { form, recordQuestion } = this, answers = {},
          blocks = document.querySelectorAll('.block-style')

      if(Object.keys(recordQuestion).length == 0) {
        return ;
      }

      this.limit = 1
      form.answers.map(its => {
        let [value] = JSON.parse(its.value)
        if(value) answers[its['choice_id']] = value.value
        else answers[its['choice_id']] = ''
      })

      for(let i = 0; i < blocks.length; i++) {
        let block = blocks[i],
            id = block.dataset.id
        if(recordQuestion[id]) {
          block.style.backgroundImage = `url(${recordQuestion[id]})`

          this.imageList.filter((its, idx) => {
            if(its.image == recordQuestion[id]) {
              block.dataset.mark = idx.toString()
              this.existImage.push(Number(idx))
            }
          })
        }
      }

      for(let i = 0; i < blocks.length; i++) {
        let block = blocks[i],
            id = block.dataset.id, url = ''
        if(block.style.backgroundImage) {
          let blockImg = block.style.backgroundImage.match(/"(.+?)"/g)[0]
          url = blockImg.substring(1, blockImg.length - 1)
        }

        if(this.mode == 'question') {
          continue ;
        }
        if(answers[id] == url) {
          block.classList.add('block-style-success')
        } else {
          block.classList.add('block-style-error')
        }
      }
    },
    addRegion() {
      this.drag.createBlock()
    },

    dragstart(e, idx) {
      e.dataTransfer.setData('info', idx)
    },
    clickEvent(e, idx) {
      if(!this.existImage.includes(idx))
        this.curClickSelectImage = idx
    },
    getNodeTotalHeight(dom) {
      if(!dom) return 0
      let styleData = window.getComputedStyle(dom, null)
      return dom.offsetHeight
          + Number(styleData.marginTop.replace('px', ''))
          + Number(styleData.marginBottom.replace('px', ''))
    },
    sizeEvent() {
      this.$nextTick(() => {
        let windowHeight = window.innerHeight,
            header = document.querySelector('.header'),
            mainHeight = document.querySelector('.main-header'),
            question = document.querySelector('.question-view'),
            quest = document.querySelector('.quest'),
            viewAnswer = document.querySelector('.viewAnswer-func')
        let imgDom = document.querySelector('.quest-img'),
            iWidth = question.clientWidth - 40 - quest.clientWidth,
            iHeight = windowHeight
                - this.getNodeTotalHeight(header)
                - this.getNodeTotalHeight(mainHeight)
                - this.getNodeTotalHeight(viewAnswer)
                - 40 - 30,
            imgBl = 833 / 500

        if(iWidth / imgBl <= iHeight) {
          iHeight = iWidth / imgBl
        }

        iHeight = iHeight > 500 ? 500 : iHeight
        iWidth = iWidth > 833 ? 833 : iWidth
        imgDom.style.height = iHeight + 'px'
        let bl = (iWidth / 833) * 500 / 300
        for(let i = 0; i < this.map.length; i++) {
          let {width, height, x, y, uuid} = this.map[i],
              block = document.querySelector(`#${uuid}`)
          if(!block) {
            continue ;
          }
          block.style.width = `${width * bl}px`
          block.style.height = `${height * bl}px`
          block.style.top = `${y * bl}px`
          block.style.left = `${x * bl}px`
        }
      })
    },
    initData() {
      this.answer_results = {
        id: null,
        student_choices: []
      }
      this.recordQuestion = {}
      this.existImage = []
      this.imageList = []

      this.drag = new ImgDrag({
        plate: '.quest-img',
        useType: 'view',
      })

      let content = JSON.parse(this.form.content)
      this.map = []
      for(let i = 0; i < this.form.choices.length; i++) {
        let its = this.form.choices[i],
            imgList = content.imageList[i]
        this.map.push({
          ...imgList,
          other: {
            id: its.id,
            position: its.position,
            type: its.type
          }
        })
      }
      content.imageList = content.imageList.filter(its => its.image && its)
      this.imageList = content.imageList
      for(let i = 0; i < this.imageList.length; i++) {
        let random = Math.floor(Math.random() * this.imageList.length)
        if(random != i) {
          [this.imageList[i], this.imageList[random]] = [this.imageList[random], this.imageList[i]]
        }
      }
      this.drag.echoBlock(this.map, this)
      setTimeout(() => this.showTrueFalse(), 500)
      this.sizeEvent()
    }
  },
  created() {

  },
  mounted() {
    this.initData()
    window.onresize = _debounce(this.sizeEvent, 500)
    if (!this.form.img_url) {
      document.documentElement.style.setProperty('--selector-width', '100%');
    } else {
      document.documentElement.style.setProperty('--selector-width', '50%');
    }
  }
}
</script>

<style scoped lang="scss">
@import url('../../static/pubile.scss');
.fixedFontSizeSelector {
  width: var(--selector-width, 50%);
}

@media (max-width: 480px) {
  .fixedFontSizeSelector {
    width: var(--selector-width, 100%);
  }
}
.question-view {
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-radius: 10px;

  /* 禁止页面回弹干扰拖拽 */
  overscroll-behavior: none;
  min-height: 0;
  -webkit-touch-callout: none;
  user-select: none;

  .quest {
    flex: 1;
    height: 100%;

    .title {
      font-size: 15px;
      color: #333;
    }

    .img-show {
      width: 440px;
      display: flex;
      align-content: flex-start;
      flex-wrap: wrap;
      margin-top: 2px;
      touch-action: none;
    }

    .single {
      position: relative;
      width: 100px;
      height: 100px;
      margin: 10px 10px 0 0;
      border-radius: 2px;
      box-shadow: 0 2px 3px 2px #ccc;
      overflow: hidden;
      cursor: pointer;
      touch-action: none;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        user-select: none;
        -webkit-user-drag: none;
        -webkit-touch-callout: none;
        pointer-events: none;
      }
    }

    .disabled {
      opacity: .4;
      cursor: not-allowed;
    }

    .cur-select {
      box-shadow: 0px 0px 10px 2px orange;
    }
  }
  .quest-img {
    margin-left: 10px;
    width: 833px;
    height: 500px;
    background-color: #fff;
    background-size: 100% 100%;
    position: relative;
    .img-region {
      position: relative;
      display: flex;
      overflow: hidden;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      span {
        position: absolute;
        background-color: #fff;
        color: #333;
      }

      &:after, &:before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: -50%;
        margin: auto;
        height: 1px;
        width: 200%;
        background-color: #fff;
        transform: rotateZ(45deg);
      }

      &:before {
        transform: rotateZ(-45deg);
      }
    }

    img {
      width: 100%;
      height: 100%;
      user-select: none;
      -webkit-user-drag: none;
    }

  }
}
</style>